import React, { useState } from "react";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { Calendar } from "lucide-react";
import dayjs from "dayjs";

import {
  AddHolidayAction,
  UpdateHolidayAction,
} from "store/middlewares/dataManagement/holiday";
import { Input, Label, Button } from "components/ui";
import { CustomSelect, SubmitLoaderButton } from "components/atoms";
import CustomDatePicker from "components/atoms/CustomDatePicker";
import { closeAdminModal } from "store/slices/common/adminModal.slice";

function ModalEditHoliday({ row }) {
  const dispatch = useDispatch();
  const [formError, setFormError] = useState(false);
  const { country } = useSelector((state) => state.country);

  const handleSubmit = (values) => {
    let tempObj = { ...values };
    if (row) {
      tempObj = { ...tempObj, id: row?.id };
      dispatch(
        UpdateHolidayAction({
          ...tempObj,
          onSuccess: () => formik.setSubmitting(false),
          onError: () => setFormError(true),
        })
      );
    } else {
      dispatch(
        AddHolidayAction({
          ...tempObj,
          onSuccess: () => formik.setSubmitting(false),
          onError: () => setFormError(true),
        })
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      title: row?.title?.trim() ? row.title.trim() : "",
      country: row?.country ? row.country : "",
      date: row?.date ? dayjs(row.date) : null,
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Holiday title is required"),
      country: Yup.string().required("Country is required"),
      date: Yup.mixed().required("Date is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit} className="grid gap-5">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <Calendar className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {row ? "Edit holiday" : "Add holiday"}
        </h2>
      </div>

      {/* Inputs */}
      <div className="grid gap-4">
        <div>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            className="!py-0 w-full mt-2"
            variant="lg"
            error={formik.errors.title && formik.touched.title}
            errorMsg={formik.errors.title}
            value={formik.values.title}
            type="text"
            name="title"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isTextOnly={true}
            placeholder="Enter title"
          />
        </div>

        <div>
          <Label htmlFor="date">Date</Label>
          <CustomDatePicker
            id="date"
            variant={"lg"}
            error={formik.errors.date && formik.touched.date}
            errorMsg={formik.errors.date}
            value={formik.values.date}
            name="date"
            onChange={(newValue) => {
              formik.setFieldValue("date", newValue);
            }}
            onBlur={formik.handleBlur}
            placeholder="Select date"
            className="!py-0 w-full mt-2"
          />
        </div>

        <div>
          <Label htmlFor="country">Country</Label>
          <CustomSelect
            id="country"
            error={formik.errors.country && formik.touched.country}
            errorMsg={formik.errors.country}
            className="!py-0 w-full mt-2 !h-10"
            val={formik.values.country}
            placeholder={row?.country || "Select country"}
            options={country?.data?.map((item) => ({
              label: item.name,
              value: item.name,
            }))}
            onChange={(selectedOption) => {
              formik.setFieldValue("country", selectedOption || "");
            }}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          btnText={row ? "Update" : "Add"}
          isDisable={formik.isSubmitting && !formError}
        />
      </div>
    </form>
  );
}

export default ModalEditHoliday;